import { Eye, History, Save, Send } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useParams } from 'react-router'
import { toast } from 'sonner'
import { apiErrorMessage } from '@/i18n'
import { ErrorState } from '@/components/common/States'
import { BackButton } from '@/components/common/BackButton'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Modal } from '@/components/ui/modal'
import { fmtDate } from '@/features/shared/format'
import { HrSurface } from '@/features/hr/personel/components/HrSurface'
import { cn } from '@/lib/utils'
import { DocumentCanvas } from './components/DocumentCanvas'
import { FileUploadZone } from './components/FileUploadZone'
import { revokePreviewUrl } from './lib/previewUrl'
import { PublishSidebar } from './components/PublishSidebar'
import { TemplatePreview } from './components/TemplatePreview'
import { ensureLocalDraft, publishTemplate, saveTemplateDraft, useCategoryLabel, useTemplateBundle } from './api'
import { applyUploadedFile, computeFieldDiff, validateForPublish } from './lib/templateHelpers'
import type { PublishMode, SablonSurum } from './types'

export function SablonSurumPage() {
  const { id } = useParams<{ id: string }>()
  const { t } = useTranslation()
  const categoryLabel = useCategoryLabel()
  const navigate = useNavigate()
  const bundle = useTemplateBundle(id)
  const sablon = bundle.data?.sablon
  const surumler = bundle.data?.surumler ?? []
  const hydrated = useRef<string | null>(null)
  const [draft, setDraft] = useState<SablonSurum | null>(null)
  const [changeReason, setChangeReason] = useState('')
  const [mode, setMode] = useState<PublishMode>('future_only')
  const [employeeIds, setEmployeeIds] = useState<string[]>([])
  const [previewOpen, setPreviewOpen] = useState(false)
  const [zoom, setZoom] = useState(0.7)

  useEffect(() => {
    if (!id || !bundle.data || hydrated.current === id) return
    hydrated.current = id
    const next = ensureLocalDraft(bundle.data.surumler, bundle.data.sablon)
    setDraft(next)
    setChangeReason(next.changeReason || '')
  }, [bundle.data, id])

  const published = useMemo(
    () => surumler.find((s) => s.status === 'yayinda'),
    [surumler],
  )

  const diffs = useMemo(() => {
    if (!published || !draft) return []
    return computeFieldDiff(published, draft).filter((d) => d.kind !== 'unchanged')
  }, [published, draft])

  if (bundle.isLoading) {
    return (
      <HrSurface>
        <div className="py-20 text-center text-sm text-muted-foreground">{t('common.loading')}</div>
      </HrSurface>
    )
  }

  if (!sablon) {
    return (
      <HrSurface>
        <ErrorState error={new Error('not_found')} onRetry={() => navigate('/hr/belgeler')} />
      </HrSurface>
    )
  }

  if (!draft) {
    return (
      <HrSurface>
        <div className="py-20 text-center text-sm text-muted-foreground">{t('common.loading')}</div>
      </HrSurface>
    )
  }

  async function saveDraft() {
    if (!draft) return
    const next = { ...draft, changeReason }
    try {
      await saveTemplateDraft(next)
      setDraft(next)
      toast.success(t('hr.belgeler.toasts.draftSaved'))
    } catch (error) {
      toast.error(apiErrorMessage(error))
    }
  }

  async function doPublish() {
    if (!draft) return
    const errors = validateForPublish(draft)
    if (errors.length) {
      toast.error(t('hr.belgeler.toasts.publishBlocked'))
      return
    }
    if (!changeReason.trim()) {
      toast.error(t('hr.belgeler.toasts.reasonRequired'))
      return
    }
    if (mode === 'selected_employees' && employeeIds.length === 0) {
      toast.error(t('hr.belgeler.toasts.selectEmployees'))
      return
    }
    try {
      const result = await publishTemplate({ ...draft, changeReason }, { changeReason, mode, employeeIds })
      if (!result.ok) {
        toast.error(t('hr.belgeler.toasts.publishBlocked'))
        return
      }
      toast.success(t('hr.belgeler.toasts.published'))
      navigate('/hr/belgeler')
    } catch (error) {
      toast.error(apiErrorMessage(error))
    }
  }

  return (
    <HrSurface density="editor">
      <div className="flex w-full flex-col gap-2">
        <div className="flex items-start gap-3">
          <BackButton to={`/hr/belgeler/sablonlar/${sablon.id}/duzenle`} className="mt-4" />
          <div className="min-w-0 space-y-0.5">
            <div className="text-[11px] text-muted-foreground">
              <Link to="/hr/belgeler" className="hover:underline">
                {t('hr.belgeler.breadcrumb.docs')}
              </Link>
              {' › '}
              <Link to="/hr/belgeler" className="hover:underline">
                {t('hr.belgeler.breadcrumb.templates')}
              </Link>
              {' › '}
              <span>{sablon.ad}</span>
              {' › '}
              <span>{t('hr.belgeler.breadcrumb.edit')}</span>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-lg font-semibold tracking-tight">
                {t('hr.belgeler.versionTitle', {
                  from: published?.version ?? sablon.currentVersion,
                  to: draft.version,
                })}
              </h1>
              <Badge tone="info">{t('hr.belgeler.status.taslak')}</Badge>
            </div>
            <p className="text-xs text-muted-foreground">
              {sablon.ad} · {categoryLabel(sablon.kategori)} · A4 ·{' '}
              {t('hr.belgeler.version.editorLocal')}
            </p>
          </div>
        </div>

        <div className="rounded-xl border border-border/70 bg-card p-2.5">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-xs font-semibold">{t('hr.belgeler.version.historyTitle')}</h2>
            <Button type="button" variant="outline" size="sm" className="h-7 bg-card text-xs" disabled>
              <History />
              {t('hr.belgeler.version.compareAction')}
            </Button>
          </div>
          <ol className="flex flex-wrap gap-2">
            {surumler.map((s) => (
              <li
                key={s.id}
                className={cn(
                  'min-w-[8rem] flex-1 rounded-lg border px-2.5 py-1.5',
                  s.id === draft.id
                    ? 'border-info bg-info/8'
                    : s.status === 'yayinda'
                      ? 'border-border/70'
                      : 'border-border/50 bg-muted/20',
                )}
              >
                <div className="flex items-center gap-2 text-sm font-semibold">
                  <span
                    className={cn(
                      'size-2 rounded-full',
                      s.id === draft.id ? 'bg-info' : s.status === 'yayinda' ? 'bg-muted-foreground' : 'bg-success',
                    )}
                  />
                  v{s.version}
                </div>
                <div className="mt-1 text-[11px] text-muted-foreground">{fmtDate(s.createdAt)}</div>
                <div className="mt-1 line-clamp-2 text-xs text-muted-foreground">
                  {s.changeReason || t('hr.belgeler.version.noReason')}
                </div>
              </li>
            ))}
          </ol>
        </div>

        <div className="grid grid-cols-[minmax(0,1fr)] gap-2 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_280px]">
          <ComparePane
            title={t('hr.belgeler.version.current', { v: published?.version ?? '—' })}
            meta={
              published?.publishedAt
                ? t('hr.belgeler.version.publishedAt', { date: fmtDate(published.publishedAt) })
                : t('hr.belgeler.version.noPublished')
            }
          >
            {published ? (
              <DocumentCanvas
                surum={published}
                pageId={published.pages[0]?.id ?? ''}
                zoom={zoom}
                onZoomChange={setZoom}
                editable={false}
                showToolbar={false}
                fillMode="empty"
              />
            ) : (
              <p className="p-4 text-sm text-muted-foreground">{t('hr.belgeler.version.noPublished')}</p>
            )}
          </ComparePane>

          <ComparePane
            title={t('hr.belgeler.version.draft', { v: draft.version })}
            meta={t('hr.belgeler.version.editedAt', { date: fmtDate(draft.createdAt) })}
            highlight
          >
            {diffs.some((d) => d.kind === 'added') ? (
              <div className="mx-2 mt-2 rounded-md border border-dashed border-success/50 bg-success/5 px-2 py-1.5 text-[11px] text-success">
                {t('hr.belgeler.version.newSectionBadge')}
                <div className="mt-0.5 text-foreground">
                  {diffs
                    .filter((d) => d.kind === 'added')
                    .map((d) => d.label)
                    .join(', ')}
                </div>
              </div>
            ) : null}
            <DocumentCanvas
              surum={draft}
              pageId={draft.pages[0]?.id ?? ''}
              zoom={zoom}
              onZoomChange={setZoom}
              editable={false}
              showToolbar={false}
              fillMode="empty"
            />
          </ComparePane>

          <div className="space-y-2">
            <PublishSidebar
              surum={draft}
              versionLabel={`v${draft.version}`}
              changeReason={changeReason}
              onChangeReason={setChangeReason}
              mode={mode}
              onModeChange={setMode}
              employeeIds={employeeIds}
              onEmployeeIdsChange={setEmployeeIds}
              showVersionInput
            />

            <div className="rounded-xl border border-border/70 bg-card p-3">
              <h3 className="text-xs font-semibold">{t('hr.belgeler.version.sourceFile')}</h3>
              <div className="mt-2 space-y-2 text-sm">
                {(
                  [
                    ['sistem', 'hr.belgeler.method.sistem'],
                    ['docx', 'hr.belgeler.method.docx'],
                    ['pdf', 'hr.belgeler.method.pdf'],
                  ] as const
                ).map(([idKey, labelKey]) => (
                  <label key={idKey} className="flex items-center gap-2">
                    <input
                      type="radio"
                      checked={draft.method === idKey}
                      onChange={() => setDraft({ ...draft, method: idKey })}
                      className="accent-primary"
                    />
                    {t(labelKey)}
                  </label>
                ))}
              </div>
              {draft.method !== 'sistem' ? (
                <div className="mt-3">
                  <FileUploadZone
                    value={draft.uploadedFile}
                    accept={draft.method === 'pdf' ? 'pdf' : 'docx'}
                    onChange={(file) => setDraft(applyUploadedFile(draft, file))}
                    onClear={() => {
                      revokePreviewUrl(draft.uploadedFile)
                      setDraft({
                        ...draft,
                        uploadedFile: undefined,
                        demoVariables: undefined,
                        variableMappings: [],
                      })
                    }}
                  />
                </div>
              ) : null}
            </div>

            {diffs.length ? (
              <div className="rounded-2xl border border-border/70 bg-card p-4 text-sm shadow-xs">
                <h3 className="mb-2 font-semibold">{t('hr.belgeler.version.diffTitle')}</h3>
                <ul className="space-y-1.5">
                  {diffs.map((d) => (
                    <li key={`${d.kind}-${d.key}`} className="flex items-center gap-2 text-xs">
                      <Badge
                        tone={d.kind === 'added' ? 'success' : d.kind === 'removed' ? 'danger' : 'warning'}
                      >
                        {t(`hr.belgeler.version.diff.${d.kind}`)}
                      </Badge>
                      <span>{d.label}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        </div>

        {draft.method === 'pdf' ? (
          <div className="rounded-2xl border border-warning/30 bg-warning/10 p-4 text-sm">
            <p className="font-medium">{t('hr.belgeler.upload.externalPdf')}</p>
            <p className="mt-1 text-xs text-muted-foreground">{t('hr.belgeler.upload.pdfCapabilities')}</p>
          </div>
        ) : null}

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border/60 pt-3">
          <Button type="button" variant="outline" className="bg-card" onClick={saveDraft}>
            <Save />
            {t('hr.belgeler.saveDraft')}
          </Button>
          <div className="flex gap-2">
            <Button type="button" variant="outline" className="bg-card" onClick={() => setPreviewOpen(true)}>
              <Eye />
              {t('hr.belgeler.previewAction')}
            </Button>
            <Button type="button" onClick={doPublish}>
              <Send />
              {t('hr.belgeler.publish.newVersion')}
            </Button>
          </div>
        </div>
      </div>

      <Modal open={previewOpen} onOpenChange={setPreviewOpen} icon={<Eye />} title={t('hr.belgeler.previewAction')} size="xl" bodyClassName="overflow-auto scrollbar-none p-0">
        <TemplatePreview surum={draft} defaultZoom={0.8} />
      </Modal>
    </HrSurface>
  )
}

function ComparePane({
  title,
  meta,
  children,
  highlight,
}: {
  title: string
  meta: string
  children: React.ReactNode
  highlight?: boolean
}) {
  return (
    <div
      className={cn(
        'overflow-hidden rounded-xl border bg-card',
        highlight ? 'border-primary/40' : 'border-border/70',
      )}
    >
      <div className="border-b border-border/70 px-2.5 py-2">
        <div className="text-xs font-semibold">{title}</div>
        <div className="text-[11px] text-muted-foreground">{meta}</div>
      </div>
      <div className="max-h-[65vh] overflow-auto scrollbar-none">{children}</div>
    </div>
  )
}
